import { z } from 'zod';

export const PurchasesEntitlementInfo = z.object({
  identifier: z.string(),
  isActive: z.boolean(),
  willRenew: z.boolean(),
  periodType: z.string(),
  latestPurchaseDate: z.string(),
  latestPurchaseDateMillis: z.number(),
  originalPurchaseDate: z.string(),
  originalPurchaseDateMillis: z.number(),
  expirationDate: z
    .string()
    .nullable()
    .transform((value) => value || null),
  expirationDateMillis: z
    .number()
    .nullable()
    .transform((value) => value || null),
  store: z.union([
    z.literal('PLAY_STORE'),
    z.literal('APP_STORE'),
    z.literal('STRIPE'),
    z.literal('MAC_APP_STORE'),
    z.literal('PROMOTIONAL'),
    z.literal('AMAZON'),
    z.literal('RC_BILLING'),
    z.literal('EXTERNAL'),
    z.literal('PADDLE'),
    z.literal('TEST_STORE'),
    z.literal('GALAXY'),
    z.literal('UNKNOWN_STORE'),
  ]),
  productIdentifier: z.string(),
  productPlanIdentifier: z
    .string()
    .nullable()
    .transform((value) => value || null),
  isSandbox: z.boolean(),
  unsubscribeDetectedAt: z
    .string()
    .nullable()
    .transform((value) => value || null),
  unsubscribeDetectedAtMillis: z
    .number()
    .nullable()
    .transform((value) => value || null),
  billingIssueDetectedAt: z
    .string()
    .nullable()
    .transform((value) => value || null),
  billingIssueDetectedAtMillis: z
    .number()
    .nullable()
    .transform((value) => value || null),
  ownershipType: z.union([z.literal('FAMILY_SHARED'), z.literal('PURCHASED'), z.literal('UNKNOWN')]),
});

export type PurchasesEntitlementInfo = z.infer<typeof PurchasesEntitlementInfo>;

/**
 * Contains all the entitlements associated to the user.
 */
export interface PurchasesEntitlementInfos {
  /**
   * Map of all EntitlementInfo (`PurchasesEntitlementInfo`) objects (active and inactive) keyed by entitlement identifier.
   */
  readonly all: {
    [key: string]: PurchasesEntitlementInfo;
  };
  /**
   * Map of active EntitlementInfo (`PurchasesEntitlementInfo`) objects keyed by entitlement identifier.
   */
  readonly active: {
    [key: string]: PurchasesEntitlementInfo;
  };
}
export interface CustomerInfo {
  /**
   * Entitlements attached to this customer info
   */
  readonly entitlements: PurchasesEntitlementInfos;
  /**
   * Set of active subscription skus
   */
  readonly activeSubscriptions: string[];
  /**
   * Set of purchased skus, active and inactive
   */
  readonly allPurchasedProductIdentifiers: string[];
  /**
   * The latest expiration date of all purchased skus
   */
  readonly latestExpirationDate: string | null;
  /**
   * The date this user was first seen in RevenueCat.
   */
  readonly firstSeen: string;
  /**
   * The original App User Id recorded for this user.
   */
  readonly originalAppUserId: string;
  /**
   * Date when this info was requested
   */
  readonly requestDate: string;
  /**
   * Map of skus to expiration dates
   */
  readonly allExpirationDates: {
    [key: string]: string | null;
  };
  /**
   * Map of skus to purchase dates
   */
  readonly allPurchaseDates: {
    [key: string]: string | null;
  };
  /**
   * Returns the version number for the version of the application when the
   * user bought the app. Use this for grandfathering users when migrating
   * to subscriptions.
   *
   * This corresponds to the value of CFBundleVersion (in iOS) in the
   * Info.plist file when the purchase was originally made. This is always null
   * in Android
   */
  readonly originalApplicationVersion: string | null;
  /**
   * Returns the purchase date for the version of the application when the user bought the app.
   * Use this for grandfathering users when migrating to subscriptions.
   */
  readonly originalPurchaseDate: string | null;
  /**
   * URL to manage the active subscription of the user. If this user has an active iOS
   * subscription, this will point to the App Store, if the user has an active Play Store subscription
   * it will point there. If there are no active subscriptions it will be null.
   * If there are multiple for different platforms, it will point to the device store.
   */
  readonly managementURL: string | null;
  readonly nonSubscriptionTransactions: PurchasesStoreTransaction[];
}
/**
 * List of all non subscription transactions. Use this to fetch the history of
 * non-subscription purchases
 */
export interface PurchasesStoreTransaction {
  /**
   * Id of the transaction.
   */
  transactionIdentifier: string;
  /**
   * Product Id associated with the transaction.
   */
  productIdentifier: string;
  /**
   * Purchase date of the transaction in ISO 8601 format.
   */
  purchaseDate: string;
}

export const PurchasesEntitlementInfos = z.object({
  all: z.record(z.string(), PurchasesEntitlementInfo),
  active: z.record(z.string(), PurchasesEntitlementInfo),
});
